import { useEffect, useState } from 'react';
import { formatDateTime, formatTime } from '../../shared/format';
import { sinceWords } from '../../shared/timeclock';
import { storageGet, storageSet } from '../lib/hooks';
import { ApiError, get, post } from '../lib/api';
import { useSession } from '../lib/session';
import { Button, Field, PinPad, Sheet, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { BrandLogo, EmployeeBadge, brandMoment } from '../ui/brand';
import { GateScene } from '../ui/scene';

const TAGLINE = 'The Operating System for Your Store.';

/** West Coast Wireless, painted on as the screen opens, with STORE OS under it. */
function Logo() {
  return (
    <div className="gate-brand">
      <BrandLogo className="gate-wcw" animate />
      <div className="gate-os">
        <img src="/assets/store-os-icon-128.png" alt="" width={20} height={20} />
        <span className="strong">STORE OS</span>
        <span>{TAGLINE}</span>
      </div>
    </div>
  );
}

/** First run: create the owner account. */
export function SetupScreen() {
  const { refresh } = useSession();
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', password: '', pin: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/api/setup', f);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed.');
    } finally {
      setBusy(false);
    }
  };
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <main className="gate">
      <GateScene />
      <form className="gate-card" onSubmit={submit}>
        <Logo />
        <div>
          <h1>Set up STORE OS</h1>
          <p className="muted">Create the owner account. You'll use the email and password to manage everything, and the PIN at the store counter.</p>
        </div>
        <div className="form-grid">
          <Field label="First name"><input className="input" value={f.firstName} onChange={set('firstName')} required autoComplete="given-name" /></Field>
          <Field label="Last name"><input className="input" value={f.lastName} onChange={set('lastName')} autoComplete="family-name" /></Field>
          <Field label="Email" className="full"><input className="input" type="email" value={f.email} onChange={set('email')} required autoComplete="email" /></Field>
          <Field label="Password" hint="10+ characters" className="full"><input className="input" type="password" value={f.password} onChange={set('password')} required minLength={10} autoComplete="new-password" /></Field>
          <Field label="Counter PIN" hint="4–6 digits" className="full"><input className="input" inputMode="numeric" pattern="\d{4,6}" value={f.pin} onChange={set('pin')} required autoComplete="off" /></Field>
        </div>
        {error ? <div className="error-text" role="alert">{error}</div> : null}
        <Button type="submit" variant="primary" size="lg" block busy={busy}>Create owner account</Button>
      </form>
    </main>
  );
}

/** Email + password sign-in for owners and managers (back office, or to register a tablet). */
export function PasswordScreen({ onBack }: { onBack?: () => void }) {
  const { refresh, status } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/login', { email, password });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="gate">
      <GateScene />
      <form className="gate-card" onSubmit={submit}>
        <Logo />
        <div>
          <h1>Sign in</h1>
          <p className="muted">Owners and managers sign in with email. Store staff use their PIN on a store tablet.</p>
        </div>
        <Field label="Email"><input className="input input-lg" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" autoFocus /></Field>
        <Field label="Password"><input className="input input-lg" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" /></Field>
        {error ? <div className="error-text" role="alert">{error}</div> : null}
        <Button type="submit" variant="primary" size="lg" block busy={busy}>Sign in</Button>
        {onBack ? <Button variant="ghost" onClick={onBack}>{status?.device ? 'Back to clock in' : 'Back'}</Button> : null}
        {status?.demo ? <p className="small muted" style={{ textAlign: 'center' }}>Demo: owner@storeos.demo / demo-owner-2026</p> : null}
        {status?.demo_site && status.demo_people?.[0]?.email ? <p className="small muted" style={{ textAlign: 'center' }}>Demo owner: {status.demo_people[0].email} / {status.demo_people[0].password}</p> : null}
      </form>
    </main>
  );
}

const SAVED_EMAIL = 'storeos.setupEmail';

/**
 * A new device: the owner (or a manager) types their email once and their 4-digit code,
 * and this device becomes the store's tablet. The email is remembered here for next time.
 */
export function TabletSetupScreen() {
  const { refresh, status } = useSession();
  const saved = storageGet<string>(SAVED_EMAIL, '');
  const [email, setEmail] = useState(saved);
  const [editEmail, setEditEmail] = useState(!saved);
  const [store, setStore] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const stores = status?.stores ?? [];
  if (usePassword) return <PasswordScreen onBack={() => setUsePassword(false)} />;
  const submit = async (pin: string) => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setEditEmail(true);
      return setError('Enter your email first.');
    }
    if (stores.length > 1 && !store) return setError('Pick the store for this tablet.');
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/tablet-setup', { email: email.trim(), pin, locationId: store || undefined });
      storageSet(SAVED_EMAIL, email.trim().toLowerCase());
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set up this tablet.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="gate">
      <GateScene />
      <div className="gate-card" style={{ maxWidth: 460 }}>
        <Logo />
        <div style={{ textAlign: 'center' }}>
          <h1>{stores.length === 1 ? `Set up the ${stores[0].name} tablet` : 'Set up this tablet'}</h1>
          <p className="muted">Owner or manager: your email, then your 4-digit code.</p>
        </div>
        {editEmail ? (
          <Field label="Email"><input className="input input-lg" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} autoComplete="username" autoFocus inputMode="email" /></Field>
        ) : (
          <div className="spread card card-tight"><span className="strong">{email}</span><button type="button" className="link-btn" onClick={() => setEditEmail(true)}>Change</button></div>
        )}
        {stores.length > 1 ? (
          <Field label="Store">
            <select className="select" value={store} onChange={(e) => setStore(e.target.value)}>
              <option value="">Pick a store</option>
              {stores.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
        ) : null}
        <PinPad onSubmit={submit} busy={busy} error={error} autoFocus={!editEmail} />
        <div className="row" style={{ justifyContent: 'center' }}>
          <button type="button" className="link-btn" onClick={() => setUsePassword(true)}>Sign in with a password instead</button>
        </div>
      </div>
    </main>
  );
}

/** The code in a setup link (#/setup/<code>), if this page was opened from one. */
export function setupLinkToken(hash = location.hash): string | null {
  return /^#\/setup\/([A-Za-z0-9_-]{16,200})$/.exec(hash)?.[1] ?? null;
}

/**
 * Opened from a setup link (Settings → Store tablets → Set up a computer at a store): one tap
 * makes this computer that store's tablet, with no owner email or code needed here.
 */
export function SetupLinkScreen({ token }: { token: string }) {
  const { refresh } = useSession();
  const [info, setInfo] = useState<{ location: string; name: string; expires_at: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    post<{ location: string; name: string; expires_at: number }>('/api/auth/setup-link/check', { token })
      .then(setInfo)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'This setup link didn’t load. Check the internet connection and try again.'));
  }, [token]);
  const leave = async () => {
    history.replaceState(null, '', '/#/');
    await refresh();
  };
  const setUp = async () => {
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/setup-link', { token });
      await leave();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Couldn’t set up this computer.');
      setBusy(false);
    }
  };
  return (
    <main className="gate">
      <GateScene />
      <div className="gate-card" style={{ maxWidth: 460 }}>
        <Logo />
        {info ? (
          <>
            <div style={{ textAlign: 'center' }}>
              <h1>Set up this computer for {info.location}</h1>
              <p className="muted">It becomes the {info.location} store tablet (“{info.name}”). Everyone who works at {info.location} signs in on it with their own code.</p>
            </div>
            <Button variant="primary" size="lg" busy={busy} onClick={setUp}>Set up this computer</Button>
            <p className="small muted" style={{ textAlign: 'center' }}>This link works once, until {formatDateTime(info.expires_at)}.</p>
          </>
        ) : !error ? <p className="muted" style={{ textAlign: 'center' }}>Checking the link…</p> : null}
        {error ? <div className="banner warn" role="alert"><Icon.alert /><div>{error}</div></div> : null}
        {error ? <Button onClick={leave}>Go to STORE OS</Button> : null}
      </div>
    </main>
  );
}

/** In the demo everyone's clock-in code is shown under their name (a real store never shows codes). */
function demoCode(status: ReturnType<typeof useSession>['status'], name: string): string | null {
  if (!status?.demo_site) return null;
  return status.demo_people?.find((p) => p.name === name)?.pin ?? null;
}

/** Store tablet lock screen: tap your name to clock in. First time, you make your own code. */
type LockPerson = NonNullable<NonNullable<ReturnType<typeof useSession>['status']>['device']>['employees'][number];

export function LockScreen() {
  const { status, refresh } = useSession();
  const [who, setWho] = useState<LockPerson | null>(null);
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  // The next person to sign in sees the logo painted on the home screen again.
  useEffect(() => { brandMoment.home = ''; }, []);
  const device = status?.device;
  if (!device) return null;
  if (usePassword) return <PasswordScreen onBack={() => setUsePassword(false)} />;
  const reset = () => { setWho(null); setFirstPin(null); setError(null); };
  const submit = async (pin: string) => {
    if (!who) return;
    setError(null);
    if (!who.has_pin) {
      // Making a code: type it twice.
      if (!firstPin) return setFirstPin(pin);
      if (pin !== firstPin) {
        setFirstPin(null);
        return setError('The two codes didn’t match. Start again.');
      }
    }
    setBusy(true);
    try {
      await post(who.has_pin ? '/api/auth/pin' : '/api/auth/create-pin', { employeeId: who.id, pin });
      await refresh();
    } catch (err) {
      setFirstPin(null);
      setError(err instanceof ApiError ? err.message : 'Could not clock in.');
    } finally {
      setBusy(false);
    }
  };
  const first = who?.name.split(' ')[0];
  const prompt = !who ? 'Tap your name to clock in.'
    : who.has_pin ? `Hi ${first}. Enter your code.${demoCode(status, who.name) ? ` (Demo: it’s ${demoCode(status, who.name)}.)` : ''}`
    : firstPin ? `Type the same 4 digits again, ${first}.` : `Welcome ${first}! Make your own 4-digit code.`;
  return (
    <main className="gate gate-lock">
      <GateScene />
      <div className={`gate-card lock-card${who ? ' lock-picked' : ''}`}>
        <Logo />
        <div style={{ textAlign: 'center' }}>
          <div className="small muted strong" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{status?.business_name} · {device.location.name}</div>
          <h1>Clock in</h1>
          <p className="muted">{prompt}</p>
        </div>
        {!who ? (
          <div className="people badges">
            {device.employees.map((e) => (
              <button key={e.id} type="button" className="person badge-tile" onClick={() => { setWho(e); setFirstPin(null); setError(null); }}>
                <EmployeeBadge person={e} />
                <span className="nm">{e.name}</span>
                {e.clocked_in_at ? <span className="in">In since {sinceWords(e.clocked_in_at)}</span> : !e.has_pin ? <span className="rl">New: make a code</span> : <span className="rl">{e.role}</span>}
                {demoCode(status, e.name) ? <span className="rl demo-code">code {demoCode(status, e.name)}</span> : null}
              </button>
            ))}
          </div>
        ) : (
          <div className="clockin">
            <EmployeeBadge key={who.id} person={who} className="clockin-badge" />
            <div className="card stack clockin-pad">
              <PinPad key={`${who.id}-${firstPin ? 2 : 1}`} onSubmit={submit} busy={busy} error={error} />
              {!who.has_pin ? <p className="small muted" style={{ textAlign: 'center' }}>Pick 4 digits only you know. Not 1234 or 1111.</p> : null}
              <Button variant="ghost" icon="chevronLeft" onClick={reset}>Not {first}?</Button>
            </div>
          </div>
        )}
        <div className="row" style={{ justifyContent: 'center' }}>
          <button type="button" className="link-btn" onClick={() => setUsePassword(true)}>Sign in with email and password</button>
        </div>
        <p className="tiny muted" style={{ textAlign: 'center' }}>Tablet: {device.register.name}</p>
      </div>
    </main>
  );
}

/** After an owner/manager signs in on a new device: make it a store tablet or keep it as a back-office computer. */
export function RegisterDeviceSheet({ onClose }: { onClose: () => void }) {
  const { me, refresh } = useSession();
  const toast = useToast();
  const [loc, setLoc] = useState(me?.location?.id ?? me?.locations[0]?.id ?? '');
  const [name, setName] = useState('Front counter');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await post('/api/registers', { locationId: loc, name });
      toast('This device is now a store tablet. Staff sign in with their PIN.', 'good');
      await refresh();
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not register this device.', 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet
      title="Make this a store tablet"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={submit} disabled={!loc || !name.trim()}>Register this device</Button></>}
    >
      <p className="muted">Store tablets stay signed in to one store. Staff tap their name and enter their PIN to sell. You'll be signed out after registering.</p>
      <Field label="Store">
        <select className="select" value={loc} onChange={(e) => setLoc(e.target.value)}>
          {me?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>
      <Field label="Name for this tablet"><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} /></Field>
    </Sheet>
  );
}

export async function ping() {
  return get('/api/status');
}

export const GateIcon = Icon.lock;
